import React, { useCallback, useMemo } from 'react';
import { Button, Icon, Modal } from '@aidkitorg/component-library';
import * as XLSX from 'xlsx';
import { useLocalizedStrings } from '../../../Localization';
import { Transaction } from '../../../Contexts/TransactionsContext';

type TransactionsExportModalProps = {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  transactions: Transaction[],
  filenamePrefix?: string,
  visibleFields?: string[],
}

const getTodayAsYYYYMMDD = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

export const TransactionsExportModal: React.FC<TransactionsExportModalProps> = ({ open, setOpen, transactions, filenamePrefix = 'Transactions', visibleFields }) => {
  const L = useLocalizedStrings();

  const allFieldsInOrder: Array<keyof Transaction> = [
    'target_field',
    'payment_provider',
    'entry_type',
    'amount',
    'subledger',
    'action_id',
    'attempt',
    'rc_status',
    'transaction_id',
    'transaction_state',
    'transaction_clear_date',
    'card_id',
    'trace_id',
    'failure_message',
    'givecard_recipient_id',
    'uid',
  ];

  const fieldsToExport = useMemo(() => {
    if (visibleFields && visibleFields.length > 0) {
      const visibleSet = new Set(visibleFields);
      return allFieldsInOrder.filter(f => visibleSet.has(f));
    }
    return allFieldsInOrder;
  }, [visibleFields]);

  const processedRows = useMemo(() => {
    return transactions.map(t => {
      const row: Record<string, unknown> = {};
      fieldsToExport.forEach((key) => {
        const value = (t as any)[key];
        row[key] = value ?? '';
      });
      return row;
    })
  }, [transactions, fieldsToExport])

  const downloadAsCSV = useCallback(() => {
    if (processedRows.length === 0) return;
    const headers = fieldsToExport as string[];
    const rows = processedRows.map(obj => headers.map(h => obj[h as keyof typeof obj]));
    const escapeCell = (val: unknown) => String(val ?? '').replace(/"/g, '""');
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${escapeCell(v)}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const fileName = `${filenamePrefix}_${getTodayAsYYYYMMDD()}.csv`;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [processedRows, filenamePrefix, fieldsToExport]);

  const downloadAsXLSX = useCallback(() => {
    if (processedRows.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(processedRows, { header: fieldsToExport as string[] });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    const fileName = `${filenamePrefix}_${getTodayAsYYYYMMDD()}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }, [processedRows, filenamePrefix, fieldsToExport]);

  return (
    <Modal title={L.admin_panel.payments.export_recipients} open={open} onClose={() => setOpen(false)}>
      <div className="pb-4">
        {L.admin_panel.payments.export_recipients_desc}
      </div>
      <div className="flex flex-col gap-y-2">
        <Button onClick={downloadAsCSV} variant="secondary" className="!justify-start">
          <div className="flex gap-4 items-center py-2">
            <Icon name="ArrowDownTrayIcon" role='decorative'/>
            <div className="!text-left"> 
              <div>{L.admin_panel.payments.csv_format}</div>
              <div className="font-light">{L.admin_panel.payments.csv_format_desc}</div>
            </div>
          </div>
        </Button>
        <Button onClick={downloadAsXLSX} variant="secondary" className="!justify-start">
          <div className="flex gap-4 items-center py-2">
            <Icon name="TableCellsIcon" role='decorative'/>
            <div className="!text-left"> 
              <div>{L.admin_panel.payments.xlsx_format}</div>
              <div className="font-light">{L.admin_panel.payments.xlsx_format_desc}</div>
            </div>
          </div>
        </Button>
      </div>
    </Modal>
  );
}

export default TransactionsExportModal;


